import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

/**
 * Action codes, HTTP verbs, & payload shapes are direct ports from
 * ui/app/components/efile/efileElements/EfileElementsController.js
 *
 *   GET (legacy `JsonObjectFactory.getJSONObj` -> `$http.get`,
 *        GlobalServices.js L2003-L2078):
 *   - SubmissionController#loadHierarchy (L521-L535)  -> 71n7lj
 *   - AddEditController#loadForms / loadLookup       -> jvpk52, onozya,
 *                                                       a2j0xa, 0uem72,
 *                                                       7bkpvk
 *   - AddEditController#loadForm                      -> s3hr77
 *     Backend expects workspace-style params merged with form keys, e.g.:
 *     activity_key, tax_year, scenario, scenarioCode, jcd_key, jcd_ta_key,
 *     screen_key, screen_type, is_locked, is_issue_key_enabled, issue_id,
 *     issue_short_desc, submission_id, key_id, form, form_sub,
 *     parent_element_name, psq_no (see loadFormData contextParams).
 *
 *   POST (legacy `JsonObjectFactory.saveJSON` -> `$http({method:'post'})`,
 *         GlobalServices.js L2224-L2312):
 *   - AddEditController#saveForm (L2556-L2635)        -> rv8nq0
 *                                                       (SAVE_EFILE_ELEMENT_OVERRIDE)
 *
 * NEVER modify these without re-checking the legacy controller — backend
 * action handlers expect both the verb and the exact field names below.
 */

export interface AddEditSaveArgs {
  submissionId: any;
  keyId: any;
  form: string;
  formSub: string;
  parentElementName: string;
  formId: string;
  comment: string;
  changes: any[];
  taxYear: any;
  scenario: any;
  jcdKey: number | string;
  ssoId: string;
}

@Injectable({
  providedIn: 'root'
})
export class AddEditService {

  constructor(private http: HttpClient) { }

  private postForm(url: string, params: Record<string, any>): Observable<any> {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const v = params[key];
      if (v === undefined || v === null) return;
      urlEncoded.set(key, typeof v === 'object' ? JSON.stringify(v) : String(v));
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * GET helper — mirrors legacy `JsonObjectFactory.getJSONObj`
   * (`$http.get(url, { params })` in `ui/app/GlobalServices.js` L2003-L2078).
   * Used for every `loadJsonObject?action_code=...` action in the legacy
   * AddEdit screen (71n7lj, onozya, jvpk52, a2j0xa, 0uem72, s3hr77, 7bkpvk).
   *
   * Object values are JSON-stringified to match how the legacy `$http.get`
   * default param-serializer flattens objects into the query string.
   */
  private getQuery(url: string, params: Record<string, any>): Observable<any> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      const v = params[key];
      if (v === undefined || v === null) return;
      httpParams = httpParams.set(key, typeof v === 'object' ? JSON.stringify(v) : String(v));
    });
    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  /**
   * Entity hierarchy for the Submission detail screen.
   * Legacy: SubmissionController.loadHierarchy (L521).
   * Response: { jsonObject: [{ KEY_DESC, PARENT, IS_LEAF, IS_ENTITY, LVL, PATH, KEY_ID, ENTITY_TYPE_ID, ... }] }
   */
  loadHierarchy(
    baseUrl: string,
    submissionId: any,
    taxYear: any,
    scenario: any,
    jcdKey: number | string,
    ssoId: string,
    contextParams: Record<string, any> = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=71n7lj`;
    return this.getQuery(url, {
      ...contextParams,
      submission_id: submissionId,
      tax_year: taxYear,
      scenario,
      jcd_key: jcdKey,
      sso_id: ssoId,
    });
  }

  /**
   * Action `onozya` — loads the full set of e-file forms valid for an entity
   * type within a given schema set. The legacy AddEditController (L2010-
   * L2018) sends the full screen / submission / issue context the same way
   * `loadHierarchy` (`71n7lj`) does, plus `entity_type_id` and
   * `schema_set_id`. Real-world payload captured from the legacy app:
   *   tax_year=2025&submission_id=4126013&screen_type=custom&screen_key=11212
   *   &schema_set_id=2025152&scenario=103&jcd_ta_key=5435&jcd_key=250
   *   &issue_short_desc=&issue_id_for_tx_save=&issue_id=&is_locked=N
   *   &entity_type_id=1&activity_key=7530
   *
   * `contextParams` should be the object produced by
   * `AddEditComponent.buildHierarchyParams` (minus `sso_id`, which the
   * legacy `onozya` call does not include) so every key above flows
   * through unchanged.
   */
  loadAllForms(
    baseUrl: string,
    entityTypeId: any,
    schemaSetId: any,
    contextParams: Record<string, any> = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=onozya`;
    return this.getQuery(url, {
      ...contextParams,
      entity_type_id: entityTypeId,
      schema_set_id: schemaSetId,
    });
  }

  loadFormsForSubmission(baseUrl: string, submissionId: any, keyId: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=jvpk52`;
    return this.getQuery(url, { submission_id: submissionId, key_id: keyId });
  }

  loadFormSubOptions(baseUrl: string, form: string, submissionId: any, keyId: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=a2j0xa`;
    return this.getQuery(url, { form, submission_id: submissionId, key_id: keyId });
  }

  loadParentElementNames(baseUrl: string, form: string, entityTypeId: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=0uem72`;
    return this.getQuery(url, { form, entity_type_id: entityTypeId });
  }

  loadFormData(
    baseUrl: string, form: string, submissionId: any, keyId: any,
    formSub: string, parentElementName: string, psqNo: any,
    contextParams: Record<string, any> = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=s3hr77`;
    return this.getQuery(url, {
      ...contextParams,
      form,
      submission_id: submissionId,
      key_id: keyId,
      form_sub: formSub,
      parent_element_name: parentElementName,
      psq_no: psqNo,
    });
  }

  /**
   * Lock-source check: legacy uses this to decide whether the form is
   * source-locked for this user (loadLookup '7bkpvk' in clickIcon, L2785).
   */
  checkLockSources(
    baseUrl: string,
    ssoId: string,
    contextParams: Record<string, any> = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=7bkpvk`;
    return this.getQuery(url, {
      ...contextParams,
      sso_id: ssoId,
    });
  }

  /**
   * Save form-element overrides.
   *
   * Legacy clob_data shape (EfileElementsController.js L2592-L2603):
   *   {
   *     submissionId, processName: 'SAVE_EFILE_ELEMENT_OVERRIDE',
   *     keyId, form, formSub, comment, pen, formId, parentSno: '0', changes
   *   }
   *
   * Legacy clob_settings (L2607-L2611):
   *   { tax_year, scenario, jcd_key }
   *
   * Plus the same triple plus sso_id is also passed at the top level of
   * the form-encoded body. Keep this in lock-step with the legacy code —
   * the rv8nq0 SP does its own lookups against these exact field names.
   */
  /**
   * Generate the schema stylesheet preview HTML for a (submissionId, formId).
   *
   * Legacy: EfileService.viewStyleSheet (L434), called from
   * AddEditController.viewStyleSheet (L2480). Backend returns
   * `{ html: '<...>' }` against the gtw_efile_api_url base URL.
   *
   * The caller is responsible for rewriting `mef/` image paths and opening
   * a Blob URL — kept in the component so we can also use the assets path
   * from `baseURLs.api`.
   */
  viewStyleSheet(efileBaseUrl: string, submissionId: any, formId: string): Observable<any> {
    const url = `${efileBaseUrl}/viewStyleSheet`;
    const params = new URLSearchParams();
    if (formId !== undefined && formId !== null) params.set('formId', String(formId));
    if (submissionId !== undefined && submissionId !== null) params.set('submissionId', String(submissionId));
    const query = params.toString();
    return this.http.get<any>(query ? `${url}?${query}` : url, { withCredentials: true });
  }

  saveFormData(baseUrl: string, args: AddEditSaveArgs): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=rv8nq0`;

    const clobData = {
      submissionId: args.submissionId,
      processName: 'SAVE_EFILE_ELEMENT_OVERRIDE',
      keyId: args.keyId,
      form: args.form,
      formSub: args.formSub,
      comment: args.comment || '',
      pen: args.parentElementName,
      formId: args.formId || '',
      parentSno: '0',
      changes: args.changes || [],
    };

    const clobSettings = {
      tax_year: args.taxYear,
      scenario: args.scenario,
      jcd_key: args.jcdKey,
    };

    // Strict legacy parity (controller L2354-L2365): the AngularJS
    // saveForm only ever serialises clob_data, clob_settings, tax_year,
    // scenario, jcd_key and sso_id at the top level. The migrated build
    // previously appended issue_id / issue_short_desc / checkLockedbj /
    // scenarioCode / is_issue_key_enabled "to keep parity with current
    // e-file save contract", but those are NOT in the legacy payload —
    // and the rv8nq0 SP works without them. Keeping them around silently
    // drifts the migrated screen away from legacy behaviour, so we strip
    // them. If a request interceptor on a sibling screen turns out to
    // need them re-introduce them globally rather than per-component.
    const params: Record<string, any> = {
      clob_data: JSON.stringify(clobData),
      clob_settings: JSON.stringify(clobSettings),
      tax_year: args.taxYear,
      scenario: args.scenario,
      jcd_key: args.jcdKey,
      sso_id: args.ssoId,
    };

    return this.postForm(url, params);
  }
}
